/**
 * The full-screen loading screen inside React: every Suspense fallback and Dashboard's
 * own loading state. Markup and CSS are the boot loader's (tcLoader.html/.css, delivered
 * by scripts/boot-loader.mjs as "virtual:tc-loader"), so nothing here draws anything.
 *
 * What this adds is continuity. One page load can pass through several loaders: the boot
 * loader in index.html, the app-level Suspense fallback, the dashboard route's fallback,
 * Dashboard's data wait. Each one here joins the running sequence by giving its CSS
 * animations the sequence's start time, so the hand-over from one to the next is
 * frame-exact and the whole wait reads as one shot. When the last loader unmounts, a
 * detached copy of it plays the exit (.tcl-x) over the content that replaced it.
 */
import React, { useLayoutEffect, useRef } from 'react';
import { LOADER_HTML } from 'virtual:tc-loader';
import { activeBrand, brandConfig } from '../../config/brand';
import { isWhiteLabel } from '../../config/resellerShop';

/** A loader gone this soon had shown nothing but its field; it leaves without an exit. */
const EXIT_AFTER_MS = 250;
/** Longest exit in tcLoader.css (.tcl-x .tcl-a: 0.56 s) plus margin. */
const EXIT_MS = 700;

let sequenceStart: number | null = null;
let mountedLoaders = 0;

const timelineNow = (): number => {
    const now = document.timeline?.currentTime;
    return typeof now === 'number' ? now : performance.now();
};

function joinSequence(): number {
    if (sequenceStart !== null) return sequenceStart;
    // Continue the boot loader when it is still up; all its animations share one start.
    const bootAnimation = document
        .querySelector('#app-loader:not(.tcl-x) .tcl-d')
        ?.getAnimations?.()[0];
    const bootStart = bootAnimation?.startTime;
    sequenceStart = typeof bootStart === 'number' ? bootStart : timelineNow();
    return sequenceStart;
}

function lockToSequence(node: HTMLElement, start: number) {
    node.getAnimations?.({ subtree: true }).forEach((animation) => {
        animation.startTime = start;
    });
}

const isMetadeck = () =>
    activeBrand === 'metadeck' || (typeof window !== 'undefined' && window.location.hostname.includes('metadeck'));

const TcBrandLoader: React.FC = () => {
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        const start = joinSequence();
        mountedLoaders += 1;
        lockToSequence(node, start);

        return () => {
            mountedLoaders -= 1;
            let ghost: HTMLElement | null = null;
            if (timelineNow() - start >= EXIT_AFTER_MS) {
                // Keep this frame on screen until the next one tells whether the wait goes on.
                ghost = node.cloneNode(true) as HTMLElement;
                ghost.removeAttribute('role');
                ghost.removeAttribute('aria-live');
                ghost.setAttribute('aria-hidden', 'true');
                // scripts/prerender.js strips it should a snapshot catch it mid-exit.
                ghost.setAttribute('data-tcl-ghost', '');
                document.body.appendChild(ghost);
                lockToSequence(ghost, start);
            }
            requestAnimationFrame(() => {
                // Another loader mounted in the same commit and carries the shot on.
                if (mountedLoaders > 0) {
                    ghost?.remove();
                    return;
                }
                sequenceStart = null;
                if (!ghost) return;
                const leaving = ghost;
                leaving.classList.add('tcl-x');
                window.setTimeout(() => leaving.remove(), EXIT_MS);
            });
        };
    }, []);

    return (
        <div
            ref={ref}
            className={isMetadeck() ? 'tcl tcl-md' : 'tcl'}
            role="status"
            aria-live="polite"
            dangerouslySetInnerHTML={{ __html: LOADER_HTML }}
        />
    );
};

/**
 * A reseller's shop (or our demo of one) never shows our wordmark: a quiet spinner in the reseller's colour
 * (the shop's colour is applied to our orange by the recolour pass) with the reseller's name.
 */
const ShopLoader: React.FC = () => (
    <div role="status" aria-label={`Loading ${brandConfig.name}`} style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'grid', placeItems: 'center', background: '#f5f6f8' }}>
        <div style={{ display: 'grid', justifyItems: 'center', gap: 14, font: '700 15px Inter, system-ui, sans-serif', color: '#1b1f26' }}>
            <span style={{ width: 34, height: 34, borderRadius: '50%', border: '3px solid #e3e6eb', borderTopColor: brandConfig.primaryColor, animation: 'wlspin .8s linear infinite' }} />
            <span>{brandConfig.name}</span>
            <style>{'@keyframes wlspin{to{transform:rotate(360deg)}}'}</style>
        </div>
    </div>
);

const BrandLoader: React.FC = () => (isWhiteLabel() ? <ShopLoader /> : <TcBrandLoader />);

export default BrandLoader;
